/* eslint-disable max-lines */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";

import ReactGA from "react-ga4";

import { LevelSelector } from "../LevelSelector";
import {
  checkEmptyObject,
  getMultiLevelField,
  isObject
} from "../../../services";
import styles from "./multiLevelSelector.scss";
import LevelSelectorLoader from "../LevelSelector/LevelSelectorLoader";
import { CategoryName, DataSource, isSubcategoryDataSource } from "../../../models/enums";
import {
  useCountries,
  useLocationChildren,
  useMakes,
  useSubcategories
} from "../../../routes/Configuration/hooks";
import {
  filterMakesBySubcategory,
  findNodePath,
  makesToNodes
} from "../../../routes/Configuration/helpers";
import { MultiLevelNodeI } from "../../../typings/configurationTypes";
import { rootReducerInterface } from "../../../typings/interfaces";

// Cascading dropdowns over one of the scoped configuration datasets:
// - make:          makes of `category` -> models
// - subcategories: lv1 -> lv2 tree of `module` / `category`
// - location:      countries -> counties -> cities -> settlements, each level
//                  fetched from configuration/locations/:id/children when its
//                  parent is selected.
// `child` is the control config chain ({ field, title, child: {...} }) from the
// UI config; options / value / number are injected into it for LevelSelector.
// `subcategoryField` (ad form, make datasource) names the ad field holding the
// subcategory the makes are narrowed to, e.g. categoryLv1Id of commercial vehicles.

const MAX_LOCATION_LEVELS = 4;

const gaLabelForMakes = (category: string) => {
  if (category === CategoryName.car) return "Top marke u autima";
  if (category === CategoryName.motorbike) return "Top marke u motorima";
  return "Top marke u autobusima";
};

// List of the fields of the control chain, top level first
const chainFields = (child: any) => {
  const fields = [];
  let current = child;
  while (current) {
    fields.push(current.field);
    current = current.child;
  }
  return fields;
};

const hasChildren = (node: any) =>
  node &&
  (node.hasChildren === true || (Array.isArray(node.children) && node.children.length > 0));

const MultiLevelSelector = props => {
  const {
    dataSource,
    module,
    category,
    child,
    selectorClassName,
    isMultiRows,
    className,
    isRequired,
    isDisabled,
    showAllLevel,
    changeForbidden,
    showPlaceholder,
    isColumn = false,
    hasAnyValue,
    getPopupContainer,
    excludeFilterOnly,
    subcategoryField
  } = props;

  const isLocation = dataSource === DataSource.location;
  const isMake = dataSource === DataSource.make;
  const isSubcategory = isSubcategoryDataSource(dataSource);

  const [state, setState] = useState({ value: {} });
  const isSetSingleValue = useRef(false);

  const fields = useMemo(() => chainFields(child), [child]);

  // ---- datasets
  // only the one field is selected, so typing elsewhere in the ad form does not re-render the picker
  const subcategoryId: string | undefined = useSelector((reduxState: rootReducerInterface) =>
    isMake && subcategoryField && reduxState.adForm && reduxState.adForm.adData
      ? reduxState.adForm.adData[subcategoryField]
      : undefined
  );
  const allMakes = useMakes(isMake ? category : undefined);
  const makes = useMemo(
    () => filterMakesBySubcategory(allMakes, subcategoryId),
    [allMakes, subcategoryId]
  );
  const subcategories = useSubcategories(
    isSubcategory ? module : undefined,
    isSubcategory ? category : undefined
  );
  const countries = useCountries(isLocation);

  const treeNodes: MultiLevelNodeI[] | undefined = useMemo(() => {
    if (isMake) return makes ? makesToNodes(makes) : undefined;
    if (isSubcategory) return subcategories;
    return undefined;
  }, [isMake, isSubcategory, makes, subcategories]);

  // A make chosen for another subcategory is cleared when the subcategory changes
  useEffect(() => {
    if (!subcategoryField || !treeNodes) return;

    const selectedMakeId = state.value[fields[0]];
    if (!selectedMakeId || treeNodes.some(node => node.id === selectedMakeId)) return;

    const cleared = {};
    fields.forEach(field => {
      cleared[field] = undefined;
    });
    setState({ value: cleared });
    props.onChange(cleared, props.parentId);
  }, [subcategoryId, treeNodes]);

  // Location levels are loaded on demand for the selected parent of each level.
  const selectedLocationIds = isLocation
    ? fields.slice(0, MAX_LOCATION_LEVELS - 1).map(field => state.value[field])
    : [];
  const countyList = useLocationChildren(isLocation ? selectedLocationIds[0] : undefined);
  const cityList = useLocationChildren(isLocation ? selectedLocationIds[1] : undefined);
  const settlementList = useLocationChildren(isLocation ? selectedLocationIds[2] : undefined);
  const locationChildren = useSelector(
    (reduxState: rootReducerInterface) => reduxState.configuration.locationChildren
  );

  const isLoaded = isLocation ? Array.isArray(countries) : Array.isArray(treeNodes);

  // Options of a level given the node selected on the parent level
  const getLevelNodes = (levelIndex: number, parentNode: any): any[] => {
    if (isLocation) {
      if (levelIndex === 0) return countries || [];
      if (!parentNode || !parentNode.hasChildren) return [];
      return locationChildren[parentNode.id] || [];
    }

    if (levelIndex === 0) return treeNodes || [];
    return parentNode && parentNode.children ? parentNode.children : [];
  };

  // Node selected on a level, searching the level list
  const findSelected = (nodes: any[], id: string) =>
    id ? nodes.find(node => node.id === id) : undefined;

  // ---- initial value
  useEffect(() => {
    const { value, child } = props;

    let localState = getValueFromSingle();

    if (!localState) {
      localState = {
        value: value || {}
      };
    }

    if (child.chosenValue) {
      localState.value[child.field] = child.chosenValue;
    }

    setState(localState);
  }, [props.value, isLoaded]);

  // Derive the whole chain from a single (deepest) id, e.g. { includeModelIds: id }
  const getValueFromSingle = () => {
    const { singleValue } = props;

    if (
      !isSetSingleValue.current &&
      isLoaded &&
      !isLocation &&
      checkEmptyObject(singleValue)
    ) {
      isSetSingleValue.current = true;

      const singleId: any = Object.values(singleValue)[0];
      const path = findNodePath(treeNodes, singleId);
      const parsedValue = {};

      if (path) {
        path.forEach((node, index) => {
          if (fields[index]) {
            parsedValue[fields[index]] = node.id;
          }
        });
      }

      return { value: path ? parsedValue : {} };
    }

    return null;
  };

  // ---- options injection (LevelSelector reads options/value/number from the chain)
  const injectOptions = (
    levelChild,
    nodes,
    childNumber,
    parentNode,
    ancestorPopular
  ) => {
    const { value } = state;

    const selectedId = value[levelChild.field];
    const childValue = {};
    levelChild.options = [];

    // a work around for multi level which has default value
    if (!props.child.chosenValue) {
      levelChild.number = childNumber;
    } else if (!levelChild.chosenValue) {
      levelChild.number = childNumber - 1 < 0 ? 0 : childNumber - 1;
    }

    childNumber += 1;

    const filteredNodes = excludeFilterOnly
      ? nodes.filter(node => !node.filterOnly)
      : nodes;

    // Once any ancestor in the chain was popular, none of its descendants
    // should be highlighted as popular — e.g. settlements under Grad Zagreb
    // (popular county) must not appear in "Top lokacije".
    const hasPopularAncestor = ancestorPopular || (parentNode && parentNode.popular);

    filteredNodes.forEach(node => {
      levelChild.options.push({
        title: node.name,
        value: node.id,
        popular: hasPopularAncestor ? false : node.popular
      });

      if (node.id === selectedId) {
        childValue[levelChild.field] = selectedId;
      }
    });
    levelChild.value = checkEmptyObject(childValue);

    if (levelChild.child) {
      const selectedNode = findSelected(nodes, selectedId);
      const levelIndex = fields.indexOf(levelChild.field) + 1;

      childNumber = injectOptions(
        levelChild.child,
        selectedNode ? getLevelNodes(levelIndex, selectedNode) : [],
        childNumber,
        selectedNode,
        hasPopularAncestor
      );
    }

    return childNumber;
  };

  const handleValueChange = (selectedOption, selectedChild) => {
    const { value } = state;
    const selectedField = selectedOption && Object.keys(selectedOption)[0];

    // If current value included new selected field, that's mean the parent level was changed.
    // So the value of current child should be clear.
    if (
      selectedField &&
      value[selectedField] &&
      checkEmptyObject(selectedChild)
    ) {
      const excludedFields = [];
      getMultiLevelField(selectedChild, excludedFields);

      const newValue = {};
      isObject(value) &&
        Object.keys(value).forEach(key => {
          if (!excludedFields.includes(key)) {
            newValue[key] = value[key];
          } else {
            newValue[key] = undefined;
          }
        });

      emmitOnChange(newValue, selectedOption, selectedChild);
    } else {
      emmitOnChange(value, selectedOption, selectedChild);
    }
  };

  // Node selected on the level of `field`, walking the chain from the top
  const findNodeByField = (value, field: string) => {
    let parentNode;
    for (let index = 0; index < fields.length; index += 1) {
      const nodes = getLevelNodes(index, parentNode);
      const node = findSelected(nodes, value[fields[index]]);
      if (fields[index] === field) {
        return node;
      }
      if (!node) {
        return undefined;
      }
      parentNode = node;
    }
    return undefined;
  };

  const emmitOnChange = (value, selectedOption, selectedChild) => {
    const { onChange, parentId, isRequired, onError } = props;
    const selectedField = Object.keys(selectedOption)[0];
    const selectedValue = selectedOption[selectedField];
    const nextValue = { ...value, ...selectedOption };

    // Track GA for popular make selection
    if (isMake && selectedValue && treeNodes) {
      const selectedMake = treeNodes.find(node => node.id === selectedValue);
      if (selectedMake && selectedMake.popular) {
        ReactGA.event({
          category: "Odabir marke",
          action: "Click",
          label: gaLabelForMakes(category)
        });
      }
    }

    if (onError && selectedValue) {
      let error;

      if (isRequired && checkEmptyObject(selectedChild)) {
        const selectedNode = findNodeByField(nextValue, selectedField);
        error = selectedNode ? (hasChildren(selectedNode) ? "Odaberite sve razine" : "") : undefined;
      }

      onError(error === "" ? undefined : error);
    }

    if (isLocation) {
      const tempValue = { ...nextValue };

      // Changing a level clears every deeper level
      const levelIndex = fields.indexOf(selectedField);
      if (levelIndex >= 0 && value[selectedField] !== selectedValue) {
        fields.slice(levelIndex + 1).forEach(field => {
          tempValue[field] = undefined;
        });
      }

      // Settlement coordinates are the fallback position of the ad on the map
      const settlementField = fields[3];
      if (
        settlementField &&
        selectedField === settlementField &&
        selectedOption[settlementField] !== value[settlementField] &&
        !value.isAutocompleteUsed
      ) {
        const settlement = findNodeByField(tempValue, settlementField);
        if (!settlement) return;

        tempValue.latitude = settlement.latitude;
        tempValue.longitude = settlement.longitude;
      }

      onChange(tempValue, parentId);
    } else {
      onChange(nextValue, parentId);
    }
  };

  const getNumber = levelChild => {
    if (!levelChild.child && levelChild.number) {
      return levelChild.number;
    }
    return getNumber(levelChild.child);
  };

  const disabledChildren = (levelChild, childNumber) => {
    levelChild.number = childNumber;

    childNumber += 1;

    if (levelChild.child) {
      childNumber = disabledChildren(levelChild.child, childNumber);
    }

    return childNumber;
  };

  if (!isLoaded) {
    const disabledCount = disabledChildren(child, 0);

    return (
      <div
        className={
          isColumn ? styles.columnWrapper : className || styles.wrapper
        }
      >
        <LevelSelectorLoader
          {...props.child}
          className={
            isColumn
              ? styles.columnSelector
              : selectorClassName || styles.selector
          }
          showPlaceholder={showPlaceholder}
          isRequired={isRequired}
          childCount={disabledCount}
          showAllLevel={showAllLevel}
          isMultiRows={isMultiRows}
          isColumn={isColumn}
        />
      </div>
    );
  }

  injectOptions(child, getLevelNodes(0, undefined), 0, undefined, false);

  const count = getNumber(child) + 1;

  // keep the on demand location lists referenced so the hooks above stay in use
  void countyList;
  void cityList;
  void settlementList;

  return (
    <div
      className={
        isColumn ? styles.columnWrapper : className || styles.wrapper
      }
    >
      <LevelSelector
        className={
          isColumn
            ? styles.columnSelector
            : selectorClassName || styles.selector
        }
        {...child}
        childCount={count}
        onChange={handleValueChange}
        showAllLevel={showAllLevel}
        isMultiRows={isMultiRows}
        isRequired={isRequired}
        isDisabled={isDisabled}
        changeForbidden={changeForbidden}
        showPlaceholder={showPlaceholder}
        isColumn={isColumn}
        hasAnyValue={hasAnyValue}
        getPopupContainer={getPopupContainer}
        showPopular={props.showPopular}
        dataSource={props.dataSource}
      />
    </div>
  );
};

MultiLevelSelector.defaultProps = {
  className: "",
  isMultiRows: false,
  isRequired: false,
  parentId: "",
  selectorClassName: "",
  singleValue: {},
  isDisabled: false,
  showAllLevel: false
};

export default MultiLevelSelector;



// WEBPACK FOOTER //
// ./src/components/FormControls/MultiLevelSelector/MultiLevelSelector.tsx